import { useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Link as RRLink, Navigate as RRNavigate, useNavigate, type NavigateOptions } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import usePathTransformerWithQuery from '~/hooks/use-path-transformer-with-query';

type ParsedUrlQueryInput = NodeJS.Dict<
	string | number | boolean | readonly string[] | readonly number[] | readonly boolean[] | null
>;

interface UrlObject {
	auth?: string;
	hash?: string;
	host?: string;
	hostname?: string;
	href?: string;
	pathname?: string;
	protocol?: string;
	search?: string;
	slashes?: boolean;
	port?: string | number;
	query?: string | ParsedUrlQueryInput;
}

export const useRouter = () => {
	const navigate = useNavigate();

	const routerMethods = useMemo(() => {
		return {
			/**
			 * Navigate to a new route by pushing it to the history stack.
			 *
			 * @param path - Target URL path
			 * @param options.state - (Deprecated) This parameter is deprecated. Use query strings or a global store instead.
			 */
			push: (path: string | UrlObject, options?: Omit<NavigateOptions, 'replace'>) => navigate(path, options),
			/**
			 * Navigate to a new route by replacing the current entry in the history stack.
			 *
			 * @param path - Target URL path
			 * @param options.state - (Deprecated) This parameter is deprecated. Use query strings or a global store instead.
			 */
			replace: (path: string | UrlObject, options?: Omit<NavigateOptions, 'replace'>) =>
				navigate(path, { replace: true, ...options }),
			back: () => navigate(-1),
			go: (n: number) => navigate(n)
		};
	}, [navigate]);

	return routerMethods;
};

export const Link = ({
	href,
	replace,
	state,
	...rest
}: { href: string | UrlObject; replace?: boolean; state?: unknown } & Omit<
	React.AnchorHTMLAttributes<HTMLAnchorElement>,
	'href'
>) => {
	return <RRLink to={href} replace={replace} state={state} {...rest} />;
};

export const Navigate = ({ to, replace, state }: { to: string | UrlObject; replace?: boolean; state?: unknown }) => {
	return <RRNavigate to={to} replace={replace} state={state} />;
};

/**
 * A component that renders a Link with the current query parameters preserved in the href.
 *
 * @component
 * @param props - Component props
 * @param props.children - The content to be rendered inside the link
 * @param props.href - The target URL or path
 * @param props.className - Optional CSS class name for styling
 * @param props.onClick - Optional click handler
 * @param {string} [activeMatch] - Optional path prefix to determine if the link is active. If provided, the link is considered active if the current pathname starts with this value
 * @returns A Link component with transformed href that preserves query parameters
 */
export const QueryEncodedLink = ({
	activeMatch,
	children,
	className,
	href,
	replace,
	state,
	...rest
}: {
	href: string | UrlObject;
	replace?: boolean;
	state?: unknown;
	activeMatch?: string;
	children?: React.ReactNode;
	classname?: string;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) => {
	const transformPathWithQuery = usePathTransformerWithQuery();
	const location = useLocation();

	const isActive = activeMatch
		? location.pathname.startsWith(activeMatch)
		: typeof href === 'string' && location.pathname === href;

	return (
		<RRLink
			className={isActive ? `active ${className || ''}` : className}
			to={transformPathWithQuery(href as string)}
			replace={replace}
			state={state}
			{...rest}
		>
			{children}
		</RRLink>
	);
};
